<template>
	<div>
		<n-popover
			v-model:show="showPopover"
			trigger="click"
			placement="bottom-end"
			content-class="p-0!"
			class="min-w-54!"
			:disabled="openModalDirectly"
			@update:show="handlePopoverShow"
		>
			<template #trigger>
				<JMButton
					:type
					:size
					:icon-name="showIcon ? 'carbon:calendar-add' : undefined"
					:icon-placement
					:icon-size="buttonIconSize"
					:label="iconOnly ? '' : buttonLabel"
					:disabled="!offices.length && !loadingOffices && !allowWithoutOffices"
					:loading="loadingOffices"
					@click.stop="handleTriggerClick"
				/>
			</template>

			<div class="flex w-78 flex-col gap-3">
				<div class="text-fs-sm p-3 pb-0 font-semibold">
					{{ $t(`appointments.suggest.title`) }}
				</div>

				<div class="px-3">
					<OfficeSelect
						v-if="offices.length > 1"
						v-model="selectedOfficeId"
						size="small"
						:clearable="false"
						:placeholder="$t(`appointments.form.placeholders.office`)"
						@update:model-value="loadSuggestedSlots()"
					/>
				</div>

				<n-spin :show="loadingSlots">
					<n-scrollbar v-if="hasSuggestedSlots" class="max-h-60">
						<div class="flex flex-col gap-4 px-4 pb-4">
							<div v-for="day in formattedDays" :key="day.date">
								<div
									class="border-divider mb-2 flex items-baseline justify-between gap-2 border-b pb-2"
								>
									<span class="text-fs-sm font-semibold capitalize">
										{{ day.weekday }}
									</span>
									<span class="text-secondary text-fs-xs font-medium">
										{{ day.label }}
									</span>
								</div>

								<div class="flex flex-wrap gap-1.5">
									<JMButton
										v-for="slot in day.slots"
										:key="slot.from"
										type="secondary"
										size="tiny"
										@click="selectSlot(slot)"
									>
										<span class="text-secondary">{{ slot.fromLabel }}</span>
										<span class="text-tertiary mx-1">–</span>
										<span class="text-secondary">{{ slot.toLabel }}</span>
									</JMButton>
								</div>
							</div>
						</div>
					</n-scrollbar>
					<EmptyList
						v-else-if="!loadingSlots"
						class="py-6!"
						icon-name="ph:calendar-slash"
						icon-size="medium"
						:subtitle="$t(`appointments.suggest.empty_title`)"
						:title="$t(`appointments.suggest.empty_subtitle`)"
					/>
				</n-spin>
			</div>

			<template #footer>
				<JMButton type="secondary" class="w-full!" @click="openManualForm()">
					{{ $t(`appointments.suggest.manual`) }}
				</JMButton>
			</template>
		</n-popover>

		<AppointmentFormModal
			v-model:show="showFormModal"
			:initial-datetime
			:show-appointment-type-tabs="!patientId"
			:initial-office-id
			:initial-patient-id="patientId ?? null"
			@saved="handleSaved()"
		/>
	</div>
</template>

<script setup lang="ts">
import type { ButtonSize } from "naive-ui"
import type { I18nString } from "@/lang/config"
import type { AppointmentSuggestedSlots } from "@/types/appointment"
import type { DateTimeString } from "@/types/common"
import { NPopover, NScrollbar, NSpin, useMessage } from "naive-ui"
import { storeToRefs } from "pinia"
import { computed, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import Api from "@/api"
import AppointmentFormModal from "@/components/calendar/AppointmentFormModal.vue"
import EmptyList from "@/components/common/empty/List.vue"
import JMButton from "@/components/common/JMButton.vue"
import OfficeSelect from "@/components/offices/OfficeSelect.vue"
import { useAuthStore } from "@/stores/auth"
import { useLocalesStore } from "@/stores/i18n"
import { useOfficesStore } from "@/stores/offices"
import { getApiErrorMessage } from "@/utils"
import { formatSuggestedDays } from "@/utils/calendar"
import dayjs from "@/utils/dayjs"

const props = withDefaults(
	defineProps<{
		patientId?: number
		type?: "primary" | "secondary" | "default"
		size?: ButtonSize
		label?: string
		buttonIconSize?: number
		officeId?: number | null
		iconOnly?: boolean
		showIcon?: boolean
		iconPlacement?: "left" | "right"
		/** Keep the button enabled with no offices; in that case skip the popover and open the modal directly. */
		allowWithoutOffices?: boolean
	}>(),
	{
		type: "secondary",
		size: "large",
		buttonIconSize: 18,
		officeId: null,
		iconOnly: false,
		showIcon: true,
		iconPlacement: "left",
		allowWithoutOffices: true
	}
)

const emit = defineEmits<{
	saved: []
}>()

const SUGGEST_RANGE_DAYS = 60

const message = useMessage()
const { t } = useI18n()

const buttonLabel = computed(
	() =>
		props.label ??
		(props.patientId
			? t<I18nString>(`patients.common.add_appointment`)
			: t<I18nString>(`appointments.common.add_appointment`))
)

const authStore = useAuthStore()
const localesStore = useLocalesStore()
const officesStore = useOfficesStore()
const { list: offices, loading: loadingOffices } = storeToRefs(officesStore)

const showPopover = ref(false)
const showFormModal = ref(false)
const loadingSlots = ref(false)
const selectedOfficeId = ref<number | null>(null)
const suggestedDays = shallowRef<AppointmentSuggestedSlots[]>([])
const initialDatetime = ref<number | null>(null)
const initialOfficeId = ref<number | null>(null)

/** Prefer `officeId` prop when present in the list; otherwise fall back to the first office. */
function syncSelectedOfficeId() {
	selectedOfficeId.value = officesStore.resolveOfficeId(props.officeId)
}

/** When enabled with no offices to suggest from, bypass the popover and open the modal straight away. */
const openModalDirectly = computed(() => props.allowWithoutOffices && !offices.value.length && !loadingOffices.value)

const hasSuggestedSlots = computed(() => suggestedDays.value.some(day => day.slots.length > 0))

const formattedDays = computed(() => formatSuggestedDays(suggestedDays.value, localesStore.locale))

function loadSuggestedSlots() {
	if (!selectedOfficeId.value) {
		suggestedDays.value = []
		return
	}

	loadingSlots.value = true

	Api.common.appointments
		.suggest({
			from: dayjs().startOf("day").toDate(),
			to: dayjs().add(SUGGEST_RANGE_DAYS, "day").endOf("day").toDate(),
			office_id: selectedOfficeId.value,
			duration: authStore.avgVisitDuration
		})
		.then(res => {
			suggestedDays.value = res.data ?? []
		})
		.catch(err => {
			suggestedDays.value = []
			message.error(getApiErrorMessage(err) || t<I18nString>(`common.misc.generic_error`))
		})
		.finally(() => {
			loadingSlots.value = false
		})
}

function handlePopoverShow(isOpen: boolean) {
	if (!isOpen) return

	loadSuggestedSlots()
}

function openFormModal(datetime: number | null) {
	initialDatetime.value = datetime
	initialOfficeId.value = selectedOfficeId.value
	showPopover.value = false
	showFormModal.value = true
}

function selectSlot(slot: { from: DateTimeString; to: DateTimeString }) {
	openFormModal(dayjs(slot.from, "YYYY-MM-DD HH:mm:ss").valueOf())
}

function openManualForm() {
	openFormModal(Date.now())
}

function handleTriggerClick() {
	if (openModalDirectly.value) {
		openManualForm()
	}
}

function handleSaved() {
	emit("saved")
}

watch([() => props.officeId, offices], syncSelectedOfficeId, { immediate: true })
</script>
